import i18n from '../i18n';
// analysisQueryBuilder — 데이터 분석의 설정으로 분석 SQL 을 만든다(docs/features/30-data-analysis.md).
//
// 분석 대상(source)은 둘이다:
//   · 테이블 — options.table = { owner, name } → FROM OWNER.NAME src
//   · 결과 탭의 쿼리 — userSql 을 인라인 뷰로 감싼다 → FROM ( … ) src
// 분석 유형(config.template):
//   · 'custom' — 컬럼별 표시·필터·그룹·집계·정렬 + 분석 함수 + PIVOT/UNPIVOT
//   · 'unique' · 'topn' · 'dup' · 'firstLast' — 전형적인 분석 로직의 틀(config.templateParams[유형])에 테이블·컬럼만 바꿔 끼운다
// 필터(WHERE)·PARALLEL 힌트·결과 행 제한은 모든 유형에 같이 걸린다.
//
// 입력:
//   config: {
//     template, templateParams,
//     rowLimit: number | null,    // 결과 행 제한 (0/null = 무제한) — ANSI FETCH FIRST n ROWS ONLY
//     parallel: { on, degree },   // /*+ PARALLEL(n) */ — Oracle·Tibero 만(PostgreSQL 은 힌트가 없다)
//     columns: [{ name, group, filter, sort, hide,
//                 dataType,                   // 'string'|'number'|'date'
//                 filterValue, filterValue2,  // value2 는 between 끝값
//                 filterMode,                 // 타입별 모드
//                 aggFn, alias,               // aggFn 에 COUNT_DISTINCT 가 있다 → COUNT(DISTINCT 컬럼)
//                 sortDir, sortPriority }],
//     analytics: [{ fn, col, n, partition: [이름], order: [{ col, dir }], alias }],
//     pivot: { mode: 'none'|'pivot'|'unpivot', aggFn, valueCol, pivotCol, values, unpivotCols, nameAlias, valueAlias, includeNulls },
//   }
//   options: {
//     dbType: 'oracle'|'tibero'|'postgresql',   // 분석 대상 연결의 종류
//     table: { owner, name },                    // 테이블이 대상일 때
//     sourceColumns: string[],                   // 결과 탭이 대상일 때 원 SQL 결과의 컬럼 이름(드라이버가 준 그대로, 순서대로)
//   }
//
// 지키는 것 (2026-09-13 점검에서 실제로 깨졌던 것들 — analysisQueryBuilder.test.js 가 고정한다):
//   - 결과 탭이 대상이면 식별자는 늘 큰따옴표로 감싼다. 드라이버가 준 이름이 정확한 대소문자라 그대로 맞는다.
//     따옴표 없이 쓰면 소문자 별칭("userId")·식 컬럼(COUNT(*))이 없는 컬럼이 된다.
//     테이블이 대상이면 딕셔너리 이름이라 그 DB 의 기본 대소문자이고 예약어가 아닐 때만 따옴표를 뺀다(읽기 쉬운 SQL).
//   - 같은 이름 컬럼(조인 결과)은 위치로 이름을 다시 붙인다. 인라인 뷰로 그냥 감싸면 Oracle 이
//     ORA-00918 로 거부한다. 패널도 같은 uniqueColumnNames 를 써야 설정의 이름과 맞는다.
//   - 날짜 끝값은 "다음 날 0시 미만" 으로 비교한다. "<= 그날" 은 그날 0시라 그날 행이 빠진다.
//   - DB 별 문법은 DIALECTS 한 곳에서만 갈린다. 모르는 DB 는 Oracle SQL 로 떨어지지 않고 거부한다.
//   - 행 제한은 ANSI(FETCH FIRST — 2026-10-28 사용자 요청, 예전에는 11g 호환 ROWNUM). Oracle 12c 이상이다.

const VALID_AGG = new Set(['SUM', 'MAX', 'MIN', 'AVG', 'COUNT', 'COUNT_DISTINCT']);
const PIVOT_AGG = new Set(['SUM', 'MAX', 'MIN', 'AVG', 'COUNT']);
const aggExpr = (fn, colSql) => (fn === 'COUNT_DISTINCT' ? `COUNT(DISTINCT ${colSql})` : `${fn}(${colSql})`);
const aggPrefix = (fn) => (fn === 'COUNT_DISTINCT' ? 'DIST_CNT' : fn);

/**
 * DB 별 문법.
 *   dateLit   — 'YYYY-MM-DD' 날짜 리터럴
 *   textExpr  — 문자열 필터(LIKE·=) 앞의 컬럼 식. PostgreSQL 은 숫자 컬럼에 LIKE 를 쓰면 연산자가
 *               없다고 실패하므로 TEXT 로 바꾼다(Oracle·Tibero 는 암시적으로 바꿔 준다).
 *   rename    — 같은 이름 컬럼을 다시 부르는 방법. 'with' = WITH src (c1, c2) AS (...),
 *               'derived' = FROM (...) src (c1, c2)
 *   hints     — 옵티마이저 힌트(PARALLEL)를 받는가
 *   pivot     — 'native' = PIVOT/UNPIVOT 문법 · 'emulate' = CASE 집계 / LATERAL VALUES 로 같은 결과
 *   natural   — 따옴표 없이 쓸 수 있는 이름(그 DB 의 기본 대소문자), caseOf — 별칭을 그 대소문자로
 */
const ORACLE_LIKE = {
  dateLit: (v) => `TO_DATE('${v}', 'YYYY-MM-DD')`,
  textExpr: (c) => c,
  rename: 'with',
  hints: true,
  pivot: 'native',
  natural: /^[A-Z][A-Z0-9_$#]*$/,
  caseOf: (s) => s.toUpperCase(),
};
const DIALECTS = {
  oracle: ORACLE_LIKE,
  tibero: ORACLE_LIKE,
  postgresql: {
    dateLit: (v) => `DATE '${v}'`,
    textExpr: (c) => `CAST(${c} AS TEXT)`,
    rename: 'derived',
    hints: false,
    pivot: 'emulate',
    natural: /^[a-z_][a-z0-9_$]*$/,
    caseOf: (s) => s.toLowerCase(),
  },
};

export const ANALYSIS_DB_TYPES = Object.keys(DIALECTS);
export const supportsHints = (dbType) => !!(DIALECTS[String(dbType || 'oracle').toLowerCase()] || {}).hints;

// 따옴표 없이 쓰면 문법이 깨지는 낱말 — 여기 있으면 따옴표를 남긴다(모자라면 따옴표가 하나 더 붙을 뿐이다)
const RESERVED = new Set(`ACCESS ADD ALL ALTER AND ANY AS ASC AUDIT BETWEEN BY CASE CHAR CHECK CLUSTER COLUMN COMMENT COMPRESS CONNECT CREATE CURRENT
DATE DECIMAL DEFAULT DELETE DESC DISTINCT DO DROP ELSE END EXCLUSIVE EXISTS FALSE FETCH FILE FLOAT FOR FROM GRANT GROUP HAVING IDENTIFIED IMMEDIATE IN
INCREMENT INDEX INITIAL INSERT INTEGER INTERSECT INTO IS JOIN LEFT LEVEL LIKE LIMIT LOCK LONG MAXEXTENTS MINUS MLSLABEL MODE MODIFY NOAUDIT NOCOMPRESS NOT
NOWAIT NULL NUMBER OF OFFLINE OFFSET ON ONLINE ONLY OPTION OR ORDER OUTER PCTFREE PIVOT PRIOR PUBLIC RAW RENAME RESOURCE REVOKE RIGHT ROW ROWID ROWNUM ROWS
SELECT SESSION SET SHARE SIZE SMALLINT START SUCCESSFUL SYNONYM SYSDATE TABLE THEN TO TRIGGER TRUE UID UNION UNIQUE UNPIVOT UPDATE USER USING VALIDATE
VALUES VARCHAR VARCHAR2 VIEW WHEN WHENEVER WHERE WITH`.split(/\s+/));

export function quoteIdent(name) {
  return `"${String(name).replace(/"/g, '""')}"`;
}

/** 딕셔너리 이름용 — 그 DB 의 기본 대소문자이고 예약어가 아니면 따옴표 없이 */
export function smartIdent(dbType) {
  const d = DIALECTS[String(dbType || 'oracle').toLowerCase()] || ORACLE_LIKE;
  return (name) => {
    const s = String(name);
    return d.natural.test(s) && !RESERVED.has(s.toUpperCase()) ? s : quoteIdent(s);
  };
}

/**
 * 같은 이름이 다시 나오면 `이름_2`, `이름_3` … 을 붙인다. 원래 있는 이름과도 겹치지 않게 고른다.
 * 화면(DataAnalysisModal)과 생성기가 **이 함수 하나**를 쓴다 — 규칙이 둘이면 설정이 가리키는
 * 이름과 SQL 의 이름이 갈린다.
 */
export function uniqueColumnNames(names) {
  const all = new Set(names);
  const used = new Set();
  return names.map((n) => {
    if (!used.has(n)) { used.add(n); return n; }
    let i = 2;
    let cand;
    do { cand = `${n}_${i++}`; } while (used.has(cand) || all.has(cand));
    used.add(cand);
    return cand;
  });
}

function escSqlString(v) {
  return String(v == null ? '' : v).replace(/'/g, "''");
}

function isBlank(v) {
  return v == null || String(v).length === 0;
}

function stripTrailingSemicolon(sql) {
  return String(sql || '').trim().replace(/;+\s*$/g, '');
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const NUM_RE = /^-?\d+(\.\d+)?$/;
const fail = (key, params) => new Error(i18n.t(`common:aqb.${key}`, params));

// 'YYYY-MM-DD' 이면서 실제로 있는 날짜만 받는다 — 2026-02-30 을 DB 에 보내면 DB 마다 다르게 실패한다
function checkedDate(v) {
  const s = String(v).trim();
  const m = DATE_RE.exec(s);
  const bad = () => fail('badDate', { value: s });
  if (!m) throw bad();
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) throw bad();
  return s;
}

function nextDay(s) {
  const [y, mo, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, d + 1)).toISOString().slice(0, 10);
}

// 문자열 필터 — LIKE 패턴 또는 = 비교
function buildStringFilter(dialect, col, mode, value) {
  if (isBlank(value)) return null;
  const v = escSqlString(value);
  const expr = dialect.textExpr(col);
  switch (mode) {
    case 'left%':  return `${expr} LIKE '%${v}'`;
    case 'right%': return `${expr} LIKE '${v}%'`;
    case 'both%':  return `${expr} LIKE '%${v}%'`;
    case 'exact':
    default:       return `${expr} = '${v}'`;
  }
}

// 숫자 필터 — 비교 연산자
function buildNumberFilter(col, mode, value, value2) {
  const num = (v) => {
    if (isBlank(v)) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const v1 = num(value);
  if (mode === 'between') {
    const v2 = num(value2);
    if (v1 == null || v2 == null) return null;
    return `${col} BETWEEN ${v1} AND ${v2}`;
  }
  if (v1 == null) return null;
  switch (mode) {
    case 'gt':  return `${col} > ${v1}`;
    case 'gte': return `${col} >= ${v1}`;
    case 'lt':  return `${col} < ${v1}`;
    case 'lte': return `${col} <= ${v1}`;
    case 'eq':
    default:    return `${col} = ${v1}`;
  }
}

// 날짜 필터 — 끝값은 다음 날 0시 미만. 컬럼에 함수를 씌우지 않아(TRUNC 는 PostgreSQL 에 없다) DB 가 갈리지 않는다.
function buildDateFilter(dialect, col, mode, value, value2) {
  const lit = dialect.dateLit;
  if (mode === 'date_between') {
    if (isBlank(value) || isBlank(value2)) return null;
    const from = checkedDate(value);
    const to = checkedDate(value2);
    return `(${col} >= ${lit(from)} AND ${col} < ${lit(nextDay(to))})`;
  }
  if (isBlank(value)) return null;
  const day = checkedDate(value);
  switch (mode) {
    case 'date_gte': return `${col} >= ${lit(day)}`;
    case 'date_lte': return `${col} < ${lit(nextDay(day))}`;
    case 'date_eq':
    default:         return `(${col} >= ${lit(day)} AND ${col} < ${lit(nextDay(day))})`;
  }
}

function buildFilterClause(dialect, q, c) {
  const dt = c.dataType || 'string';
  const colSql = q(c.name);
  if (dt === 'number') return buildNumberFilter(colSql, c.filterMode || 'eq', c.filterValue, c.filterValue2);
  if (dt === 'date')   return buildDateFilter(dialect, colSql, c.filterMode || 'date_eq', c.filterValue, c.filterValue2);
  return buildStringFilter(dialect, colSql, c.filterMode || 'both%', c.filterValue);
}

// ── 분석 대상 · 공통 조각 ─────────────────────────────────────────

/** FROM 절(과 이름을 다시 붙일 때의 WITH 머리) · 식별자 규칙 */
function sourceOf(userSql, options, dbType, dialect) {
  const table = options.table;
  if (table && table.name) {
    const q = smartIdent(dbType);
    return { head: [], from: [`FROM ${table.owner ? `${q(table.owner)}.` : ''}${q(table.name)} src`], q };
  }
  if (!userSql || typeof userSql !== 'string' || !userSql.trim()) throw fail('emptySql');
  const inner = stripTrailingSemicolon(userSql);
  // 같은 이름 컬럼이 있을 때만 위치로 이름을 다시 붙인다(없으면 예전과 같은 인라인 뷰)
  const srcNames = Array.isArray(options.sourceColumns) ? options.sourceColumns.filter(n => n != null && n !== '') : [];
  const uniq = uniqueColumnNames(srcNames);
  const needRename = uniq.some((n, i) => n !== srcNames[i]);
  const renameList = uniq.map(quoteIdent).join(', ');
  if (needRename && dialect.rename === 'with') {
    return { head: [`WITH src (${renameList}) AS (`, `  ${inner}`, ')'], from: ['FROM src'], q: quoteIdent };
  }
  return { head: [], from: ['FROM (', `  ${inner}`, needRename ? `) src (${renameList})` : ') src'], q: quoteIdent };
}

function parallelHint(dialect, parallel) {
  if (!dialect.hints || !parallel || !parallel.on) return '';
  const deg = Math.floor(Number(parallel.degree));
  return Number.isFinite(deg) && deg > 1 ? `/*+ PARALLEL(${Math.min(deg, 512)}) */ ` : '/*+ PARALLEL */ ';
}

function limitLines(n) {
  const limit = Number(n);
  return Number.isFinite(limit) && limit > 0 ? [`FETCH FIRST ${Math.floor(limit)} ROWS ONLY`] : [];
}

const indent = (lines) => lines.map(l => `  ${l}`);

/** 하나의 SELECT 블록 — 비어 있는 절은 뺀다 */
function selectBlock({ select, from, where = [], groupBy = [], having = null, orderBy = [], tail = [] }) {
  const out = [`SELECT ${select}`, ...from];
  if (where.length) out.push(`WHERE ${where.join(' AND ')}`);
  if (groupBy.length) out.push(`GROUP BY ${groupBy.join(', ')}`);
  if (having) out.push(`HAVING ${having}`);
  if (orderBy.length) out.push(`ORDER BY ${orderBy.join(', ')}`);
  return [...out, ...tail];
}

// ── 직접 구성 ───────────────────────────────────────────────────

export const ANALYTIC_FUNCTIONS = ['ROW_NUMBER', 'RANK', 'DENSE_RANK', 'NTILE', 'LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE', 'SUM', 'AVG', 'MIN', 'MAX', 'COUNT'];
const NEEDS_ORDER = new Set(['ROW_NUMBER', 'RANK', 'DENSE_RANK', 'NTILE', 'LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE']);
const NEEDS_COL = new Set(['LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE', 'SUM', 'AVG', 'MIN', 'MAX']);
export const analyticTakesCol = (fn) => NEEDS_COL.has(fn) || fn === 'COUNT';
export const analyticTakesN = (fn) => fn === 'NTILE' || fn === 'LAG' || fn === 'LEAD';

export function analyticAlias(a) {
  if (a.alias) return a.alias;
  const fn = String(a.fn || '').toUpperCase();
  if (fn === 'ROW_NUMBER') return 'RN';
  if (fn === 'RANK') return 'RNK';
  if (fn === 'DENSE_RANK') return 'DRNK';
  if (fn === 'NTILE') return `NTILE_${Math.max(1, Math.floor(Number(a.n)) || 4)}`;
  return a.col ? `${fn}_${a.col}` : `${fn}_ALL`;
}

function analyticExpr(a, q, check) {
  const fn = String(a.fn || '').toUpperCase();
  if (!ANALYTIC_FUNCTIONS.includes(fn)) throw fail('badAnalytic', { fn });
  const partition = (a.partition || []).filter(Boolean);
  const order = (a.order || []).filter(o => o && o.col);
  if (NEEDS_ORDER.has(fn) && !order.length) throw fail('analyticNeedsOrder', { fn });
  if (NEEDS_COL.has(fn) && !a.col) throw fail('analyticNeedsCol', { fn });
  [...partition, ...order.map(o => o.col), ...(a.col ? [a.col] : [])].forEach(check);
  let args = '';
  if (fn === 'NTILE') args = String(Math.max(1, Math.floor(Number(a.n)) || 4));
  else if (fn === 'LAG' || fn === 'LEAD') args = `${q(a.col)}, ${Math.max(1, Math.floor(Number(a.n)) || 1)}`;
  else if (fn === 'COUNT') args = a.col ? q(a.col) : '*';
  else if (NEEDS_COL.has(fn)) args = q(a.col);
  const over = [];
  if (partition.length) over.push(`PARTITION BY ${partition.map(q).join(', ')}`);
  if (order.length) over.push(`ORDER BY ${order.map(o => `${q(o.col)} ${dirOf(o.dir)}`).join(', ')}`);
  return `${fn}(${args}) OVER (${over.join(' ')})`;
}

const dirOf = (d) => (String(d || 'ASC').toUpperCase() === 'DESC' ? 'DESC' : 'ASC');

function sortedSortCols(cols) {
  return cols.filter(c => c.sort).slice().sort((a, b) => {
    const pa = (a.sortPriority == null ? 999 : Number(a.sortPriority));
    const pb = (b.sortPriority == null ? 999 : Number(b.sortPriority));
    return pa - pb;
  });
}

function buildCustom(ctx) {
  const { cols, q, src, where, hint, config } = ctx;
  const pv = config.pivot || {};
  if (pv.mode === 'pivot') return buildPivot(ctx);
  if (pv.mode === 'unpivot') return buildUnpivot(ctx);

  const visible = cols.filter(c => !c.hide);
  const grouping = visible.filter(c => c.group);
  const nonGroup = visible.filter(c => !c.group);
  const hasGroupBy = grouping.length > 0;
  const aggAlias = (c, fn) => c.alias || `${aggPrefix(fn)}_${c.name}`;

  // SELECT projection
  const projParts = [];
  for (const c of grouping) {
    projParts.push(c.alias ? `${q(c.name)} AS ${q(c.alias)}` : q(c.name));
  }
  for (const c of nonGroup) {
    const fn = (c.aggFn || 'NONE').toUpperCase();
    if (hasGroupBy && (!fn || fn === 'NONE')) throw fail('needAggregate', { name: c.name });
    if (VALID_AGG.has(fn)) {
      projParts.push(`${aggExpr(fn, q(c.name))} AS ${q(aggAlias(c, fn))}`);
    } else {
      projParts.push(c.alias ? `${q(c.name)} AS ${q(c.alias)}` : q(c.name));
    }
  }
  // 분석 함수 — 행 단위다. 그룹과 함께면 무엇을 나누는지가 갈려 막는다(그룹 결과에 순위를 매기려면 그룹 뒤 결과를 다시 분석한다)
  const analytics = (config.analytics || []).filter(a => a && a.fn);
  if (analytics.length && hasGroupBy) throw fail('analyticWithGroup');
  // 그룹 없이 집계하면 표 전체가 한 행이다 — 집계하지 않은 컬럼·분석 함수와 섞으면 ORA-00937 이다
  const aggregated = nonGroup.filter(c => VALID_AGG.has((c.aggFn || 'NONE').toUpperCase()));
  if (!hasGroupBy && aggregated.length && (aggregated.length < nonGroup.length || analytics.length)) {
    const plain = nonGroup.find(c => !VALID_AGG.has((c.aggFn || 'NONE').toUpperCase()));
    throw fail('aggWithoutGroup', { name: plain ? plain.name : analyticAlias(analytics[0]) });
  }
  for (const a of analytics) projParts.push(`${analyticExpr(a, q, ctx.check)} AS ${q(analyticAlias(a))}`);
  if (projParts.length === analytics.length) projParts.unshift(analytics.length ? 'src.*' : '*');

  // ORDER BY — 그룹핑할 때는 표시된 그룹 컬럼이나 집계 별칭만 정렬할 수 있다.
  // 숨긴 컬럼을 고르면 DB 가 ORA-00979 로 거부하니 여기서 먼저 알린다.
  const orderParts = sortedSortCols(cols).map(c => {
    const dir = dirOf(c.sortDir);
    if (!hasGroupBy && !aggregated.length) return `${q(c.name)} ${dir}`;
    const fn = (c.aggFn || 'NONE').toUpperCase();
    if (!c.hide && c.group) return `${q(c.name)} ${dir}`;
    if (!c.hide && VALID_AGG.has(fn)) return `${q(aggAlias(c, fn))} ${dir}`;
    throw fail('sortNeedsGroup', { name: c.name });
  });

  return selectBlock({
    select: `${hint}${projParts.join(', ')}`,
    from: src.from,
    where,
    groupBy: hasGroupBy ? grouping.map(c => q(c.name)) : [],
    orderBy: orderParts,
    tail: limitLines(config.rowLimit),
  });
}

/** PIVOT 값 목록 — 쉼표로 가른다. 따옴표로 감싼 값 안의 쉼표는 값이다 */
export function splitPivotValues(text) {
  const out = [];
  const s = String(text || '');
  let cur = '';
  let quoted = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "'") {
      if (quoted && s[i + 1] === "'") { cur += "'"; i++; continue; }
      quoted = !quoted;
      continue;
    }
    if (ch === ',' && !quoted) { out.push(cur.trim()); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur.trim());
  return out.filter(v => v.length > 0);
}

function pivotLiteral(dialect, v, dataType) {
  if (dataType === 'number' && NUM_RE.test(v)) return v;
  if (dataType === 'date' && DATE_RE.test(v)) return dialect.dateLit(checkedDate(v));
  return `'${escSqlString(v)}'`;
}

function buildPivot(ctx) {
  const { cols, q, src, where, hint, config, dialect, check } = ctx;
  const pv = config.pivot || {};
  const fn = String(pv.aggFn || 'SUM').toUpperCase();
  if (!PIVOT_AGG.has(fn)) throw fail('badAnalytic', { fn });
  if (!pv.pivotCol || !pv.valueCol) throw fail('pivotNeedsCols');
  check(pv.pivotCol); check(pv.valueCol);
  const values = splitPivotValues(pv.values);
  if (!values.length) throw fail('pivotNeedsValues');
  const pivotType = (cols.find(c => c.name === pv.pivotCol) || {}).dataType;
  const keep = cols.filter(c => !c.hide && c.name !== pv.pivotCol && c.name !== pv.valueCol).map(c => c.name);
  const items = values.map(v => ({ lit: pivotLiteral(dialect, v, pivotType), alias: quoteIdent(v.slice(0, 30)) }));
  const keepSet = new Set(keep);
  const orderBy = sortedSortCols(cols).filter(c => keepSet.has(c.name)).map(c => `${q(c.name)} ${dirOf(c.sortDir)}`);

  if (dialect.pivot === 'native') {
    const inner = selectBlock({ select: [...keep, pv.pivotCol, pv.valueCol].map(q).join(', '), from: src.from, where });
    return [
      `SELECT ${hint}*`, 'FROM (', ...indent(inner), ')',
      'PIVOT (',
      `  ${aggExpr(fn, q(pv.valueCol))} FOR ${q(pv.pivotCol)} IN (${items.map(it => `${it.lit} AS ${it.alias}`).join(', ')})`,
      ')',
      ...(orderBy.length ? [`ORDER BY ${orderBy.join(', ')}`] : []),
      ...limitLines(config.rowLimit),
    ];
  }
  // PIVOT 문법이 없는 DB — 값마다 CASE 로 골라 집계하면 같은 결과다
  const cases = items.map(it => `${aggExpr(fn, `CASE WHEN ${q(pv.pivotCol)} = ${it.lit} THEN ${q(pv.valueCol)} END`)} AS ${it.alias}`);
  return selectBlock({
    select: `${hint}${[...keep.map(q), ...cases].join(', ')}`,
    from: src.from,
    where,
    groupBy: keep.map(q),
    orderBy,
    tail: limitLines(config.rowLimit),
  });
}

function buildUnpivot(ctx) {
  const { cols, q, src, where, hint, config, dialect, check } = ctx;
  const pv = config.pivot || {};
  const ucols = (pv.unpivotCols || []).filter(Boolean);
  if (!ucols.length) throw fail('unpivotNeedsCols');
  ucols.forEach(check);
  const uset = new Set(ucols);
  const keep = cols.filter(c => !c.hide && !uset.has(c.name)).map(c => c.name);
  const nameAlias = q(dialect.caseOf(pv.nameAlias || 'COL_NAME'));
  const valueAlias = q(dialect.caseOf(pv.valueAlias || 'COL_VALUE'));
  const keepSet = new Set(keep);
  const orderBy = sortedSortCols(cols).filter(c => keepSet.has(c.name)).map(c => `${q(c.name)} ${dirOf(c.sortDir)}`);

  if (dialect.pivot === 'native') {
    const inner = selectBlock({ select: [...keep, ...ucols].map(q).join(', '), from: src.from, where });
    return [
      `SELECT ${hint}*`, 'FROM (', ...indent(inner), ')',
      `UNPIVOT${pv.includeNulls ? ' INCLUDE NULLS' : ''} (`,
      `  ${valueAlias} FOR ${nameAlias} IN (${ucols.map(c => `${q(c)} AS '${escSqlString(c)}'`).join(', ')})`,
      ')',
      ...(orderBy.length ? [`ORDER BY ${orderBy.join(', ')}`] : []),
      ...limitLines(config.rowLimit),
    ];
  }
  // UNPIVOT 문법이 없는 DB — 행마다 (이름, 값) 목록을 옆으로 붙인다. 값의 형식이 달라도 되게 글자로 맞춘다
  const vals = ucols.map(c => `('${escSqlString(c)}', CAST(src.${q(c)} AS TEXT))`).join(', ');
  return selectBlock({
    select: `${[...keep.map(c => `src.${q(c)}`), `u.${nameAlias}`, `u.${valueAlias}`].join(', ')}`,
    from: [...src.from, `CROSS JOIN LATERAL (VALUES ${vals}) AS u (${nameAlias}, ${valueAlias})`],
    where: [...where, ...(pv.includeNulls ? [] : [`u.${valueAlias} IS NOT NULL`])],
    orderBy,
    tail: limitLines(config.rowLimit),
  });
}

// ── 템플릿 — 전형적인 분석 로직 ─────────────────────────────────────

export const TEMPLATES = ['custom', 'unique', 'topn', 'dup', 'firstLast'];

function needCols(list, key) {
  const out = (list || []).filter(Boolean);
  if (!out.length) throw fail(key);
  return out;
}

const TEMPLATE_BUILDERS = {
  /** UNIQUE 건수 — 전체 건수와 컬럼마다 DISTINCT 건수 */
  unique(ctx, p) {
    const { q, al, src, where, hint, check } = ctx;
    const cs = needCols(p.cols, 'tplNeedCols');
    cs.forEach(check);
    const parts = [`COUNT(*) AS ${q(al('TOTAL_CNT'))}`, ...cs.map(c => `COUNT(DISTINCT ${q(c)}) AS ${q(al(`DIST_${c}`.slice(0, 30)))}`)];
    return selectBlock({ select: `${hint}${parts.join(', ')}`, from: src.from, where });
  },
  /** Top N — 'count' = 값 조합의 건수가 많은 순 · 'value' = 컬럼 값이 큰(작은) 순 */
  topn(ctx, p) {
    const { q, al, src, where, hint, check } = ctx;
    const cs = needCols(p.cols, 'tplNeedCols');
    cs.forEach(check);
    const n = Math.max(1, Math.floor(Number(p.n)) || 10);
    const dir = dirOf(p.dir || 'DESC');
    const nulls = dir === 'DESC' ? ' NULLS LAST' : '';
    if (p.by === 'value') {
      return selectBlock({ select: `${hint}*`, from: src.from, where, orderBy: cs.map(c => `${q(c)} ${dir}${nulls}`), tail: limitLines(n) });
    }
    const cnt = q(al('CNT'));
    return selectBlock({
      select: `${hint}${[...cs.map(q), `COUNT(*) AS ${cnt}`].join(', ')}`,
      from: src.from, where, groupBy: cs.map(q),
      orderBy: [`${cnt} ${dir}`, ...cs.map(q)],
      tail: limitLines(n),
    });
  },
  /** 중복 건 — COUNT(*) 분석 함수로 같은 키의 건수를 붙이고 2건 이상만 */
  dup(ctx, p) {
    const { q, al, src, where, hint, check, config } = ctx;
    const cs = needCols(p.cols, 'tplNeedCols');
    cs.forEach(check);
    const dupCnt = q(al('DUP_CNT'));
    const inner = selectBlock({ select: `src.*, COUNT(*) OVER (PARTITION BY ${cs.map(q).join(', ')}) AS ${dupCnt}`, from: src.from, where });
    return [
      `SELECT ${hint}*`, 'FROM (', ...indent(inner), ') d',
      `WHERE ${dupCnt} > 1`,
      `ORDER BY ${[`${dupCnt} DESC`, ...cs.map(q)].join(', ')}`,
      ...limitLines(config.rowLimit),
    ];
  },
  /** 최종/최초 건 — ROW_NUMBER 로 키마다 순서 컬럼이 가장 큰(작은) 한 건 */
  firstLast(ctx, p) {
    const { q, al, src, where, hint, check, config } = ctx;
    const keys = needCols(p.keys, 'tplNeedKeys');
    if (!p.orderCol) throw fail('tplNeedOrderCol');
    [...keys, p.orderCol].forEach(check);
    const dir = p.which === 'first' ? 'ASC' : 'DESC';
    const rn = q(al('RN'));
    const inner = selectBlock({
      select: `src.*, ROW_NUMBER() OVER (PARTITION BY ${keys.map(q).join(', ')} ORDER BY ${q(p.orderCol)} ${dir}${dir === 'DESC' ? ' NULLS LAST' : ''}) AS ${rn}`,
      from: src.from, where,
    });
    return [
      `SELECT ${hint}*`, 'FROM (', ...indent(inner), ') d',
      `WHERE ${rn} = 1`,
      `ORDER BY ${keys.map(q).join(', ')}`,
      ...limitLines(config.rowLimit),
    ];
  },
};

export function buildAnalysisSql(userSql, config, options = {}) {
  const dbType = String(options.dbType || 'oracle').toLowerCase();
  const dialect = DIALECTS[dbType];
  if (!dialect) throw fail('unsupportedDb', { dbType });
  const cfg = config || {};
  const cols = Array.isArray(cfg.columns) ? cfg.columns : [];
  const src = sourceOf(userSql, options, dbType, dialect);
  const q = src.q;

  // 설정이 가리키는 컬럼이 대상에 있는가 — 대상을 바꾼 뒤 옛 컬럼이 남으면 DB 가 아니라 여기서 말한다
  const known = new Set(cols.map(c => c.name));
  const check = (name) => { if (known.size && !known.has(name)) throw fail('unknownColumn', { name }); };

  const where = [];
  for (const c of cols) {
    if (!c.filter) continue;
    const clause = buildFilterClause(dialect, q, c);
    if (clause) where.push(clause);
  }

  const ctx = {
    dialect, cols, q, src, where, check, config: cfg,
    hint: parallelHint(dialect, cfg.parallel),
    // 템플릿이 붙이는 별칭 — 그 DB 의 기본 대소문자로(따옴표가 붙지 않게)
    al: (s) => dialect.caseOf(s),
  };
  const tpl = cfg.template && cfg.template !== 'custom' ? TEMPLATE_BUILDERS[cfg.template] : null;
  if (cfg.template && cfg.template !== 'custom' && !tpl) throw fail('badTemplate', { name: cfg.template });
  const body = tpl ? tpl(ctx, (cfg.templateParams || {})[cfg.template] || {}) : buildCustom(ctx);
  return [...src.head, ...body].join('\n');
}

// 타입별 필터 모드 정의 — UI 가 dropdown 옵션 구성에 사용
export const FILTER_MODES_BY_TYPE = {
  string: [
    { value: 'right%', label: i18n.t('common:aqb.startsWith') },
    { value: 'both%',  label: i18n.t('common:aqb.contains') },
    { value: 'left%',  label: i18n.t('common:aqb.endsWith') },
    { value: 'exact',  label: i18n.t('common:aqb.exact') },
  ],
  number: [
    { value: 'eq',      label: '=' },
    { value: 'gt',      label: '>' },
    { value: 'gte',     label: '>=' },
    { value: 'lt',      label: '<' },
    { value: 'lte',     label: '<=' },
    { value: 'between', label: 'BETWEEN' },
  ],
  date: [
    { value: 'date_eq',      label: i18n.t('common:aqb.sameDay') },
    { value: 'date_gte',     label: '>=' },
    { value: 'date_lte',     label: '<=' },
    { value: 'date_between', label: 'BETWEEN' },
  ],
};

export const DATA_TYPES = [
  { value: 'string', label: i18n.t('common:aqb.text') },
  { value: 'number', label: i18n.t('common:aqb.number') },
  { value: 'date',   label: i18n.t('common:aqb.date') },
];

export const AGG_FUNCTIONS = [
  { value: 'NONE',  label: i18n.t('common:aqb.none') },
  { value: 'SUM',   label: 'SUM' },
  { value: 'MAX',   label: 'MAX' },
  { value: 'MIN',   label: 'MIN' },
  { value: 'AVG',   label: 'AVG' },
  { value: 'COUNT', label: 'COUNT' },
  { value: 'COUNT_DISTINCT', label: 'COUNT(DISTINCT)' },
];

// PostgreSQL 어댑터는 형식을 'oid:<번호>' 로 준다(backend postgresAdapter). 숫자형은 드라이버가
// 문자열로 돌려주는 경우가 많아(numeric·bigint) 표본값만 보면 문자로 잘못 추정한다.
const PG_NUMBER_OIDS = new Set([20, 21, 23, 26, 700, 701, 790, 1700]);
const PG_DATE_OIDS = new Set([1082, 1114, 1184]);
const PG_TEXT_OIDS = new Set([18, 19, 25, 1042, 1043]);

// dataType 자동 추정 — metaData 의 dbTypeName / fetchType 또는 sampleRow 의 값 typeof 기반
export function inferDataType(metaItem, sampleValue) {
  // 1) metaData 의 type 힌트
  const typeName = String(metaItem?.dbTypeName || metaItem?.fetchType || metaItem?.type || '').toUpperCase();
  const oid = /^OID:(\d+)$/.exec(typeName);
  if (oid) {
    const n = Number(oid[1]);
    if (PG_NUMBER_OIDS.has(n)) return 'number';
    if (PG_DATE_OIDS.has(n)) return 'date';
    if (PG_TEXT_OIDS.has(n)) return 'string';
  } else if (typeName) {
    if (/DATE|TIMESTAMP|TIME/.test(typeName)) return 'date';
    if (/NUMBER|INT|FLOAT|DOUBLE|DECIMAL|NUMERIC/.test(typeName)) return 'number';
    if (/CHAR|TEXT|CLOB|VARCHAR|STRING/.test(typeName)) return 'string';
  }
  // 2) sampleValue typeof
  if (sampleValue != null) {
    if (typeof sampleValue === 'number') return 'number';
    if (sampleValue instanceof Date) return 'date';
    if (typeof sampleValue === 'string') {
      // YYYY-MM-DD 또는 ISO 형식이면 date 추정
      if (/^\d{4}-\d{2}-\d{2}/.test(sampleValue)) return 'date';
    }
  }
  return 'string';
}
